import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
export type ButtonSize = 'small' | 'medium' | 'large';

interface StandardButtonProps extends Omit<HTMLMotionProps<"button">, 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const StandardButton: React.FC<StandardButtonProps> = React.memo(({
  children,
  variant = 'primary',
  size = 'medium',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  disabled,
  className = '',
  ...props
}) => {
  const { isDark } = useTheme();

  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2 text-base',
    large: 'px-6 py-3 text-lg'
  };

  // Aurora dark mode: violet primary, cyan secondary, glowing effects
  const variantClasses = {
    primary: isDark
      ? 'bg-aurora-violet-600 text-white hover:bg-aurora-violet-500 focus:ring-aurora-violet-500 hover:shadow-aurora-violet disabled:bg-aurora-violet-800/50'
      : 'bg-professional-blue-600 text-white hover:bg-professional-blue-700 focus:ring-professional-blue-500 disabled:bg-professional-blue-300',

    secondary: isDark
      ? 'bg-aurora-cyan-600/20 text-aurora-cyan-300 border border-aurora-cyan-500/30 hover:bg-aurora-cyan-600/30 hover:border-aurora-cyan-500/50 focus:ring-aurora-cyan-500 hover:shadow-aurora-cyan disabled:bg-aurora-cyan-900/10'
      : 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 disabled:bg-gray-100',

    danger: isDark
      ? 'bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 hover:border-red-500/50 focus:ring-red-500 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)] disabled:bg-red-900/10'
      : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-300',

    success: isDark
      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 hover:border-emerald-500/50 focus:ring-emerald-500 hover:shadow-[0_0_20px_rgba(52,211,153,0.3)] disabled:bg-emerald-900/10'
      : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 disabled:bg-green-300',

    ghost: isDark
      ? 'bg-transparent text-aurora-violet-300 hover:bg-aurora-violet-500/10 hover:text-aurora-violet-200 focus:ring-aurora-violet-500/30'
      : 'bg-transparent text-gray-900 hover:bg-gray-100 focus:ring-gray-500'
  };

  const widthClass = fullWidth ? 'w-full' : '';
  const disabledClass = disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';

  return (
    <motion.button
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${widthClass} ${disabledClass} ${className}`}
      disabled={disabled || isLoading}
      whileHover={!disabled && !isLoading ? { scale: 1.02 } : {}}
      whileTap={!disabled && !isLoading ? { scale: 0.98 } : {}}
      {...props}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading...
        </>
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </motion.button>
  );
});

StandardButton.displayName = 'StandardButton';

export default StandardButton;